import { useMemo } from 'react'
import type { WorkspaceActivityRecord } from '../../../../services/activityService'
import type { AnalysisRowResolution, AnalysisTimeRangeKey } from '../../../../types/analysis'
import type {
  LiveModuleHeadline,
  LiveModuleRecord,
  LiveModuleSparkline,
} from '../LiveModuleCard'
import { bucketize, calculateDelta } from './bucketize'

const SENSITIVE_TYPES = new Set([
  'REVOKE_AUTHORIZATION',
  'EXPORT',
  'EXPORT_RAW_CSV',
  'INVITE',
  'ROLE_CHANGE',
  'SECURITY_ANOMALY',
  'DELETE',
])

const isSensitiveRecord = (record: WorkspaceActivityRecord) => {
  const actType = (record.activityType || '').toUpperCase()
  const action = (record.action || '').toUpperCase()
  if (SENSITIVE_TYPES.has(actType) || SENSITIVE_TYPES.has(action)) return true
  return (
    actType.includes('REVOKE') ||
    actType.includes('DELETE') ||
    actType.includes('EXPORT') ||
    actType.includes('ROLE_CHANGE') ||
    actType.includes('INVITE') ||
    actType.includes('SECURITY') ||
    action.includes('REVOKE') ||
    action.includes('DELETE') ||
    action.includes('EXPORT') ||
    action.includes('ROLE') ||
    action.includes('INVITE') ||
    action.includes('SECURITY')
  )
}

export type UseSensitiveOpsModuleParams = {
  workspaceId: string
  timeRange: AnalysisTimeRangeKey
  auditRecords: WorkspaceActivityRecord[]
  loading?: boolean
  error?: Error | null
  now?: Date
  resolutions?: Map<string, AnalysisRowResolution>
}

export const useSensitiveOpsModule = ({
  timeRange,
  auditRecords,
  loading = false,
  error = null,
  now,
  resolutions,
}: UseSensitiveOpsModuleParams) => {
  return useMemo(() => {
    const sensitive = auditRecords.filter(isSensitiveRecord)
    const timestamps = sensitive.map((r) => r.createdAt)
    const points = bucketize(timestamps, timeRange, now)
    const delta = calculateDelta(points)

    // Top 5 most recent sensitive events
    const records: LiveModuleRecord[] = [...sensitive]
      .filter((item) => !resolutions?.has(`sensitive-ops:${item.id}`))
      .sort((a, b) => {
        const timeA = a.createdAt instanceof Date ? a.createdAt.getTime() : 0
        const timeB = b.createdAt instanceof Date ? b.createdAt.getTime() : 0
        return timeB - timeA
      })
      .slice(0, 5)
      .map((item, index) => {
        const actor = item.actorName?.trim() || item.actorId || 'Workspace member'
        const actionLabel = item.action || item.activityType || 'Sensitive operation'
        const targetLabel = item.targetTitle?.trim() || item.nodeType || 'Item'
        return {
          id: item.id || `sensitive-op-${index}`,
          primary: `${actor} · ${actionLabel}`,
          secondary: targetLabel,
          drillTarget: 'SUMMARY',
        }
      })

    const headline: LiveModuleHeadline = {
      value: sensitive.length,
      delta,
      label: 'ops',
    }

    const sparkline: LiveModuleSparkline = {
      points,
      direction: 'up-is-bad',
    }

    return {
      headline,
      sparkline,
      records,
      loading,
      error,
    }
  }, [auditRecords, timeRange, now, loading, error, resolutions])
}
